import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { marketplaceAPI } from '../services/api';
import '../styles/Payment.css';

const SellerOnboarding = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [countries, setCountries] = useState([]);
  const [country, setCountry] = useState('');
  const [changingCountry, setChangingCountry] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    checkSellerStatus();
    marketplaceAPI.getSellerCountries()
      .then((res) => setCountries(res.data.countries || []))
      .catch(() => setCountries([]));
  }, []);

  const checkSellerStatus = async () => {
    try {
      setLoading(true);
      const response = await marketplaceAPI.getSellerStatus();
      setStatus(response.data);
    } catch (err) {
      setError(t('seller.statusError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!country) {
      setError(t('seller.selectCountry', 'Veuillez sélectionner votre pays'));
      return;
    }
    try {
      setLoading(true);
      setError('');
      await marketplaceAPI.createSellerAccount(country);
      // Récupérer le lien d'onboarding et rediriger
      const linkResponse = await marketplaceAPI.getOnboardingLink();
      window.location.href = linkResponse.data.url;
    } catch (err) {
      setError(err.response?.data?.message || t('seller.createError'));
      setLoading(false);
    }
  };

  const handleContinueOnboarding = async () => {
    try {
      setLoading(true);
      setError('');
      const linkResponse = await marketplaceAPI.getOnboardingLink();
      window.location.href = linkResponse.data.url;
    } catch (err) {
      setError(err.response?.data?.message || t('seller.linkError'));
      setLoading(false);
    }
  };

  const handleGoToStripeDashboard = async () => {
    try {
      setLoading(true);
      const response = await marketplaceAPI.getStripeDashboardLink();
      window.open(response.data.url, '_blank');
    } catch (err) {
      setError(err.response?.data?.message || t('seller.dashboardError'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-container">
        <div className="payment-card">
          <div className="loading-spinner">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  // Si onboarding complet
  if (status?.chargesEnabled && status?.payoutsEnabled) {
    return (
      <div className="payment-container">
        <div className="payment-card success-card">
          <div className="success-icon">&#10003;</div>
          <h2>{t('seller.accountActive')}</h2>
          <p>{t('seller.accountActiveDesc')}</p>

          <div className="seller-status-info">
            <div className="status-item success">
              <span className="status-icon">&#10003;</span>
              <span>{t('seller.paymentsActive')}</span>
            </div>
            <div className="status-item success">
              <span className="status-icon">&#10003;</span>
              <span>{t('seller.transfersActive')}</span>
            </div>
          </div>

          <div className="button-group">
            <button className="btn-primary" onClick={() => navigate('/my-products')}>
              {t('seller.myProducts')}
            </button>
            <button className="btn-secondary" onClick={() => navigate('/wallet')}>
              {t('seller.myWallet')}
            </button>
            <button className="btn-stripe" onClick={handleGoToStripeDashboard} disabled={loading}>
              {t('seller.stripeAccess')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Si compte créé mais pas terminé
  if (status?.hasAccount && !status?.detailsSubmitted) {
    const accountCountryName =
      countries.find((c) => c.code === status.country)?.name || status.country;

    return (
      <div className="payment-container">
        <div className="payment-card">
          <h2>{t('seller.completeAccount')}</h2>
          <p>{t('seller.completeAccountDesc')}</p>

          <div className="seller-info-box">
            <h3>{t('seller.whySeller')}</h3>
            <ul>
              <li>{t('seller.whyReason1')}</li>
              <li>{t('seller.whyReason2')}</li>
              <li>{t('seller.whyReason3')}</li>
            </ul>
          </div>

          {/* Le pays du compte est figé chez Stripe. Tant que l'onboarding n'est pas
              terminé, on laisse le vendeur le corriger : le compte sera recréé. */}
          {status.country && (
            <div className="seller-country-current">
              <p>
                {t('seller.currentCountry', {
                  country: accountCountryName,
                  defaultValue: `Votre compte est enregistré en : ${accountCountryName}`,
                })}
              </p>
              <button
                type="button"
                className="btn-link"
                onClick={() => setChangingCountry((v) => !v)}
                disabled={loading}
              >
                {t('seller.wrongCountry', 'Ce n\'est pas le bon pays ?')}
              </button>
            </div>
          )}

          {changingCountry && (
            <div className="form-group seller-country-select">
              <label htmlFor="seller-country-fix">
                {t('seller.countryLabel', 'Pays où vous recevrez vos paiements')}
              </label>
              <select
                id="seller-country-fix"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                disabled={loading}
              >
                <option value="">
                  {t('seller.countryPlaceholder', '— Choisissez votre pays —')}
                </option>
                {countries
                  .filter((c) => c.code !== status.country)
                  .map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
              </select>
              <p className="legal-text">
                {t(
                  'seller.countryChangeWarning',
                  'Le pays d\'un compte Stripe ne peut pas être modifié. Votre compte actuel sera supprimé et recréé dans le nouveau pays. Vous recommencerez l\'inscription depuis le début.'
                )}
              </p>
              <button
                className="btn-primary btn-large"
                onClick={handleCreateAccount}
                disabled={loading || !country}
              >
                {t('seller.recreateAccount', 'Recréer mon compte dans ce pays')}
              </button>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}

          {!changingCountry && (
            <button
              className="btn-primary btn-large"
              onClick={handleContinueOnboarding}
              disabled={loading}
            >
              {t('seller.continueRegistration')}
            </button>
          )}
        </div>
      </div>
    );
  }

  // Pas de compte - afficher la page d'accueil onboarding
  return (
    <div className="payment-container">
      <div className="payment-card">
        <h2>{t('seller.becomeSeller')}</h2>
        <p className="subtitle">
          {t('seller.becomeSellerDesc')}
        </p>

        <div className="seller-benefits">
          <div className="benefit-item">
            <span className="benefit-icon">&#128176;</span>
            <div>
              <h4>{t('seller.freeToSell')}</h4>
              <p>{t('seller.freeToSellDesc')}</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">&#128274;</span>
            <div>
              <h4>{t('seller.securePayments')}</h4>
              <p>{t('seller.securePaymentsDesc')}</p>
            </div>
          </div>
          <div className="benefit-item">
            <span className="benefit-icon">&#9989;</span>
            <div>
              <h4>{t('seller.instantEarnings')}</h4>
              <p>{t('seller.instantEarningsDesc')}</p>
            </div>
          </div>
        </div>

        <div className="fee-info-box">
          <h3>{t('seller.howItWorks')}</h3>
          <p><strong>{t('seller.sellerFee')}</strong></p>
          <p><strong>{t('seller.buyerFee')}</strong></p>
          <p className="example">
            {t('seller.example')}
          </p>
        </div>

        <div className="form-group seller-country-select">
          <label htmlFor="seller-country">
            {t('seller.countryLabel', 'Pays où vous recevrez vos paiements')}
          </label>
          <select
            id="seller-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={loading}
          >
            <option value="">
              {t('seller.countryPlaceholder', '— Choisissez votre pays —')}
            </option>
            {countries.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button
          className="btn-primary btn-large"
          onClick={handleCreateAccount}
          disabled={loading || !country}
        >
          {t('seller.createAccount')}
        </button>

        <p className="legal-text">
          {t('seller.stripeDisclaimer')}
        </p>
      </div>
    </div>
  );
};

export default SellerOnboarding;
